import { useCallback, useEffect, useState } from 'react';
import { Globe2, KeyRound, Link2, Loader2, Plus, RefreshCw, Trash2, Wifi } from 'lucide-react';
import { api, ApiError } from '../api/client.js';
import { DomainsTab } from './DnsDomains.jsx';

/**
 * /dns — DNS Manager (Spaceship registrar + Cloudflare linker).
 *
 * Phase A: accounts manager (credentials → test → save encrypted) +
 * read-only domain list per Spaceship account with NS chips and a
 * "CF linked" badge. The one-click Cloudflare link flow lands in Phase B.
 * 2026-10-23: a Domains tab (every domain of every account + its records).
 */
export function DnsManager() {
  const [accounts, setAccounts] = useState(null);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [tab, setTab] = useState('domains');

  const load = useCallback(async () => {
    try {
      const data = await api('/api/dns/accounts');
      setAccounts(data.accounts);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'network_error');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const tabCls = (t) => `px-3 py-1.5 text-sm rounded-md ${tab === t ? 'bg-s-blue text-blue font-medium' : 'text-t2 hover:bg-s-2'}`;
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1" role="tablist">
          <button type="button" role="tab" aria-selected={tab === 'domains'} className={tabCls('domains')} onClick={() => setTab('domains')}>Domains</button>
          <button type="button" role="tab" aria-selected={tab === 'accounts'} className={tabCls('accounts')} onClick={() => setTab('accounts')}>Accounts</button>
        </div>
        {tab === 'accounts' && <button
          type="button"
          onClick={() => setShowForm((v) => !v)}
          className="inline-flex items-center gap-1.5 bg-blue text-white text-sm px-3 py-1.5 rounded-md hover:bg-blue-deep"
        >
          <Plus className="w-4 h-4" /> Add account
        </button>}
      </div>

      {tab === 'domains' && <DomainsTab />}

      {tab === 'accounts' && <>
      <p className="text-sm text-t3">Domains at the registrar (Spaceship) & one-click Cloudflare linking.</p>
      {showForm && <AddDnsAccountForm onCreated={() => { setShowForm(false); load(); }} />}

      {error && (
        <div className="bg-err-bg border border-err-bd text-err text-sm rounded-md px-4 py-3">
          {error}
        </div>
      )}

      {accounts === null ? (
        <p className="text-sm text-t3">Loading…</p>
      ) : accounts.length === 0 ? (
        <div className="bg-white border border-bd rounded-lg p-8 text-center text-sm text-t3">
          <Globe2 className="w-8 h-8 mx-auto mb-3 text-ic-light" />
          No DNS accounts yet. Add your Spaceship (registrar) and Cloudflare
          accounts to manage domains and link them to Cloudflare.
        </div>
      ) : (
        <DnsAccountsTable accounts={accounts} onChanged={load} />
      )}
      </>}
    </div>
  );
}

const PROVIDER_META = {
  spaceship: { name: 'Spaceship', kind: 'registrar', cls: 'bg-s-3 text-t2 border-bd-strong' },
  cloudflare: { name: 'Cloudflare', kind: 'DNS', cls: 'bg-s-3 text-t2 border-bd-strong' },
};

function ProviderBadge({ provider }) {
  const meta = PROVIDER_META[provider] ?? { name: provider, cls: 'bg-s-3 text-t2 border-bd' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${meta.cls}`}>
      {meta.name}
    </span>
  );
}

function StatusBadge({ status }) {
  const styles = {
    ok: 'bg-s-green text-ok border-bd',
    failed: 'bg-err-bg text-err border-err-bd',
    new: 'bg-s-3 text-t2 border-bd',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${styles[status] ?? styles.new}`}>
      {status}
    </span>
  );
}

function DnsAccountsTable({ accounts, onChanged }) {
  const cfAccounts = accounts.filter((a) => a.provider === 'cloudflare');
  return (
    <div className="bg-white border border-bd rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-t3 border-b border-bd">
            <th className="px-4 py-3">Provider</th>
            <th className="px-4 py-3">Label</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Last checked</th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((a) => (
            <DnsAccountRow key={a.id} account={a} cfAccounts={cfAccounts} onChanged={onChanged} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DnsAccountRow({ account, cfAccounts, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [domainsOpen, setDomainsOpen] = useState(false);

  async function handleTest() {
    setBusy(true);
    setTestResult(null);
    try {
      const r = await api(`/api/dns/accounts/${account.id}/test`, { method: 'POST' });
      setTestResult({ ok: true, detail: r.detail });
    } catch (err) {
      setTestResult({ ok: false, detail: err.body?.detail ?? err.message });
    } finally {
      setBusy(false);
      onChanged();
    }
  }

  async function handleDelete() {
    const typed = window.prompt(
      `Type the account label to confirm deletion:\n${account.label}\n\n(Credentials are removed from the panel — nothing changes at the provider.)`,
    );
    if (typed === null) return;
    if (typed !== account.label) {
      window.alert('Label did not match — not deleted.');
      return;
    }
    setBusy(true);
    try {
      await api(`/api/dns/accounts/${account.id}`, {
        method: 'DELETE',
        body: { confirm_label: typed },
      });
      onChanged();
    } catch (err) {
      window.alert(`Delete failed: ${err.message}`);
      setBusy(false);
    }
  }

  return (
    <>
    <tr className="border-b border-bd last:border-0">
      <td className="px-4 py-3"><ProviderBadge provider={account.provider} /></td>
      <td className="px-4 py-3 font-medium text-t1">{account.label}</td>
      <td className="px-4 py-3">
        <StatusBadge status={account.status} />
        {testResult && (
          <span className={`ml-2 text-xs ${testResult.ok ? 'text-ok' : 'text-err'}`}>
            {testResult.detail}
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-t3 text-xs">
        {account.last_checked_at ? new Date(`${account.last_checked_at}Z`).toLocaleString() : '—'}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-1.5">
          {account.provider === 'spaceship' && (
            <button
              type="button"
              onClick={() => setDomainsOpen((v) => !v)}
              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-bd-blue text-blue hover:bg-s-blue"
            >
              <Globe2 className="w-3.5 h-3.5" /> Domains
            </button>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={handleTest}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-bd text-t2 hover:bg-s-2 disabled:opacity-50"
          >
            {busy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Wifi className="w-3.5 h-3.5" />}
            Test
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={handleDelete}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-err-bd text-err hover:bg-err-bg disabled:opacity-50"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </div>
      </td>
    </tr>
    {domainsOpen && (
      <tr className="border-b border-bd last:border-0 bg-s-2">
        <td colSpan={5} className="px-4 py-4">
          <DomainsPanel account={account} cfAccounts={cfAccounts} />
        </td>
      </tr>
    )}
    </>
  );
}

// ─── Domains panel (Phase A: read-only list) ────────────────────────────────

function DomainsPanel({ account, cfAccounts }) {
  const [domains, setDomains] = useState(null);
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [cfAccountId, setCfAccountId] = useState(cfAccounts[0]?.id ?? '');
  const [linking, setLinking] = useState(false);
  const [linkResults, setLinkResults] = useState(null);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const data = await api(`/api/dns/accounts/${account.id}/domains`);
      setDomains(data.domains);
      setTruncated(!!data.truncated);
      setError(null);
    } catch (err) {
      setError(err.body?.detail ?? err.message);
    } finally {
      setRefreshing(false);
    }
  }, [account.id]);

  useEffect(() => { load(); }, [load]);

  const selectedNames = (domains ?? []).filter((d) => selected.has(d.name)).map((d) => d.name);

  function toggleSelected(name) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  }

  function toggleSelectAll() {
    setSelected((prev) =>
      prev.size === (domains?.length ?? 0) ? new Set() : new Set(domains.map((d) => d.name)),
    );
  }

  async function handleLink() {
    if (!selectedNames.length || !cfAccountId) return;
    const cfLabel = cfAccounts.find((a) => a.id === Number(cfAccountId))?.label ?? 'Cloudflare';
    const okGo = window.confirm(
      `Link ${selectedNames.length} domain(s) to Cloudflare account "${cfLabel}"?\n\n${selectedNames.map((n) => `• ${n}`).join('\n')}\n\nFor each domain: a Cloudflare zone is created (if missing) and the nameservers at Spaceship are replaced with the assigned Cloudflare pair. Existing DNS served by the old nameservers stops once the change propagates.`,
    );
    if (!okGo) return;
    setLinking(true);
    setLinkResults(null);
    try {
      const resp = await api(`/api/dns/accounts/${account.id}/link`, {
        method: 'POST',
        body: { domains: selectedNames, cloudflare_account_id: Number(cfAccountId) },
      });
      setLinkResults(resp.results);
      setSelected(new Set());
      load();
    } catch (err) {
      setError(err.body?.detail ?? err.message);
    } finally {
      setLinking(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm font-semibold text-t1">
          Domains on {account.label}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {selectedNames.length > 0 && (
            <>
              <select
                value={cfAccountId}
                onChange={(e) => setCfAccountId(e.target.value)}
                className="text-xs rounded-md border border-bd-strong px-2 py-1 bg-white"
                title="Cloudflare account to link into"
              >
                {cfAccounts.length === 0 && <option value="">no CF account</option>}
                {cfAccounts.map((a) => (
                  <option key={a.id} value={a.id}>CF: {a.label}</option>
                ))}
              </select>
              <button
                type="button"
                disabled={linking || !cfAccountId || cfAccounts.length === 0}
                onClick={handleLink}
                title={cfAccounts.length === 0 ? 'Add a Cloudflare account first' : undefined}
                className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-blue text-white hover:bg-blue-deep disabled:opacity-50"
              >
                {linking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
                Link to Cloudflare ({selectedNames.length})
              </button>
            </>
          )}
          <button
            type="button"
            onClick={load}
            disabled={refreshing}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-bd text-t2 hover:bg-s-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {linkResults && (
        <div className="bg-white border border-bd rounded-md px-4 py-3 text-sm space-y-1">
          <div className="font-semibold text-t1 mb-1">Link results</div>
          {linkResults.map((r) => (
            <div key={r.name} className="flex items-start gap-2 text-xs">
              <span className={r.ok ? 'text-ok' : 'text-err'}>{r.ok ? '✓' : '✗'}</span>
              <span className="font-medium">{r.name}</span>
              {r.ok ? (
                <span className="text-t3">
                  {r.zone_created ? 'zone created' : 'zone existed'} · NS → {r.name_servers.join(', ')}
                  {r.zone_status && r.zone_status !== 'active' ? ` · zone status: ${r.zone_status} (activates after NS propagation)` : ''}
                </span>
              ) : (
                <span className="text-err">{r.detail ?? r.error}</span>
              )}
            </div>
          ))}
          <button type="button" onClick={() => setLinkResults(null)} className="text-xs text-t3 underline mt-1">
            dismiss
          </button>
        </div>
      )}

      {error && (
        <div className="bg-err-bg border border-err-bd text-err text-sm rounded-md px-3 py-2">{error}</div>
      )}

      {domains === null ? (
        <p className="text-sm text-t3">Loading…</p>
      ) : domains.length === 0 ? (
        <p className="text-sm text-t3 bg-white border border-bd rounded-md px-4 py-4 text-center">
          No domains on this account.
        </p>
      ) : (
        <div className="bg-white border border-bd rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-t3 border-b border-bd">
                <th className="px-3 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={domains.length > 0 && selected.size === domains.length}
                    onChange={toggleSelectAll}
                    title="Select all"
                  />
                </th>
                <th className="px-3 py-2">Domain</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Expires</th>
                <th className="px-3 py-2">Auto-renew</th>
                <th className="px-3 py-2">Nameservers</th>
                <th className="px-3 py-2">Cloudflare</th>
              </tr>
            </thead>
            <tbody>
              {domains.map((d) => (
                <tr key={d.name} className="border-b border-bd last:border-0">
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selected.has(d.name)}
                      onChange={() => toggleSelected(d.name)}
                    />
                  </td>
                  <td className="px-3 py-2 font-medium text-t1">{d.name}</td>
                  <td className="px-3 py-2">
                    <DomainStatusBadge status={d.status} />
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <ExpiryCell date={d.expiration_date} />
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {d.auto_renew === null ? '—' : d.auto_renew ? (
                      <span className="text-ok">on</span>
                    ) : (
                      <span className="text-t3">off</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {d.nameservers.length === 0 ? (
                        <span className="text-xs text-t3">—</span>
                      ) : (
                        d.nameservers.map((ns) => (
                          <span
                            key={ns}
                            className="inline-flex items-center px-1.5 py-0.5 rounded bg-s-3 border border-bd text-[11px] font-mono text-t2"
                          >
                            {ns}
                          </span>
                        ))
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {d.cf_linked ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border bg-s-green text-ok border-bd">
                        <Link2 className="w-3 h-3" /> linked
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border bg-s-3 text-t3 border-bd">
                        not linked
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {truncated && (
        <p className="text-xs text-warn">Note: more domains than listed — pagination cap reached.</p>
      )}
      <p className="text-xs text-t3">
        Select domains → pick the Cloudflare account → Link. Zones are created
        if missing and Spaceship nameservers switch to the assigned CF pair.
      </p>
    </div>
  );
}

function ExpiryCell({ date }) {
  if (!date) return <span className="text-t2">—</span>;
  const d = new Date(date);
  const days = Math.floor((d.getTime() - Date.now()) / 86_400_000);
  if (days < 0) {
    return <span className="text-err font-medium">{d.toLocaleDateString()} (expired)</span>;
  }
  if (days < 7) {
    return <span className="text-err font-medium">{d.toLocaleDateString()} ({days}d left)</span>;
  }
  if (days < 30) {
    return <span className="text-warn font-medium">{d.toLocaleDateString()} ({days}d left)</span>;
  }
  return <span className="text-t2">{d.toLocaleDateString()}</span>;
}

function DomainStatusBadge({ status }) {
  if (!status) return <span className="text-xs text-t3">—</span>;
  const isOk = /registered|active/i.test(status);
  const isGrace = /grace|redemption|expir/i.test(status);
  const cls = isOk
    ? 'bg-s-green text-ok border-bd'
    : isGrace
      ? 'bg-warn-bg text-[#8B5206] border-warn-bd'
      : 'bg-s-3 text-t2 border-bd';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${cls}`}>
      {status}
    </span>
  );
}

// ─── Add account form (dynamic credential fields per provider) ──────────────

function AddDnsAccountForm({ onCreated }) {
  const [provider, setProvider] = useState('spaceship');
  const [label, setLabel] = useState('');
  const [fields, setFields] = useState({ api_key: '', api_secret: '', api_token: '', account_id: '' });
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState(null);

  const credentials =
    provider === 'spaceship'
      ? { api_key: fields.api_key.trim(), api_secret: fields.api_secret.trim() }
      : { api_token: fields.api_token.trim(), account_id: fields.account_id.trim() };

  const credsFilled = Object.values(credentials).every((v) => v.length > 0);
  const canTest = credsFilled && !testing && !saving;
  const canSave = testResult?.ok && label.trim().length >= 2 && !testing && !saving;

  function setField(k, v) {
    setFields((f) => ({ ...f, [k]: v }));
    setTestResult(null);
    setError(null);
  }

  async function handleTest() {
    setTesting(true);
    setError(null);
    setTestResult(null);
    try {
      const r = await api('/api/dns/accounts/test', {
        method: 'POST',
        body: { provider, credentials },
      });
      setTestResult({ ok: true, detail: r.detail });
    } catch (err) {
      setTestResult({ ok: false, detail: err.body?.detail ?? err.message });
    } finally {
      setTesting(false);
    }
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api('/api/dns/accounts', {
        method: 'POST',
        body: { provider, label: label.trim(), credentials },
      });
      onCreated();
    } catch (err) {
      setError(err.body?.detail?.join?.(', ') ?? err.body?.detail ?? err.message);
      setSaving(false);
    }
  }

  const inputCls = 'mt-1 w-full rounded-md border border-bd-strong px-2.5 py-1.5 text-sm';

  return (
    <form onSubmit={handleSave} className="bg-white border border-bd rounded-lg p-5 space-y-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-t1">
        <KeyRound className="w-4 h-4 text-t3" />
        Add DNS account
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <label className="block">
          <span className="text-xs font-medium text-t2">Provider</span>
          <select
            value={provider}
            onChange={(e) => { setProvider(e.target.value); setTestResult(null); setError(null); }}
            className={`${inputCls} bg-white`}
          >
            <option value="spaceship">Spaceship (registrar)</option>
            <option value="cloudflare">Cloudflare</option>
          </select>
        </label>

        <label className="block">
          <span className="text-xs font-medium text-t2">Label</span>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={provider === 'spaceship' ? 'Spaceship main' : 'CF main'}
            className={inputCls}
          />
        </label>

        {provider === 'spaceship' ? (
          <>
            <label className="block">
              <span className="text-xs font-medium text-t2">API key</span>
              <input
                type="password" autoComplete="off"
                value={fields.api_key}
                onChange={(e) => setField('api_key', e.target.value)}
                placeholder="stored encrypted"
                className={`${inputCls} font-mono`}
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-t2">API secret</span>
              <input
                type="password" autoComplete="off"
                value={fields.api_secret}
                onChange={(e) => setField('api_secret', e.target.value)}
                placeholder="stored encrypted"
                className={`${inputCls} font-mono`}
              />
            </label>
          </>
        ) : (
          <>
            <label className="block">
              <span className="text-xs font-medium text-t2">API token</span>
              <input
                type="password" autoComplete="off"
                value={fields.api_token}
                onChange={(e) => setField('api_token', e.target.value)}
                placeholder="stored encrypted"
                className={`${inputCls} font-mono`}
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-t2">Account ID</span>
              <input
                type="text" autoComplete="off"
                value={fields.account_id}
                onChange={(e) => setField('account_id', e.target.value)}
                placeholder="32-char hex from the CF dashboard"
                className={`${inputCls} font-mono`}
              />
            </label>
          </>
        )}
      </div>

      {testResult && (
        <div
          className={`text-sm rounded-md px-3 py-2 border ${
            testResult.ok
              ? 'bg-s-green border-bd text-ok'
              : 'bg-err-bg border-err-bd text-err'
          }`}
        >
          {testResult.ok ? <>Credentials OK — {testResult.detail}</> : <>Test failed: {testResult.detail}</>}
        </div>
      )}
      {error && (
        <div className="text-sm rounded-md px-3 py-2 border bg-err-bg border-err-bd text-err">
          {error}
        </div>
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={!canTest}
          onClick={handleTest}
          className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-bd-strong text-t2 hover:bg-s-2 disabled:opacity-50"
        >
          {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wifi className="w-4 h-4" />}
          Test connection
        </button>
        <button
          type="submit"
          disabled={!canSave}
          title={canSave ? undefined : 'Run a successful connection test first'}
          className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-blue text-white hover:bg-blue-deep disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Save account
        </button>
        <span className="text-xs text-t3 ml-1">
          Credentials are encrypted with the panel master key and never shown again.
        </span>
      </div>
    </form>
  );
}
